import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Stream from 'flarum/common/utils/Stream';
import Model from 'flarum/common/Model';
import Discussion from 'flarum/common/models/Discussion';
import extractText from 'flarum/common/utils/extractText';
import Button from 'flarum/common/components/Button';
import Select from 'flarum/common/components/Select';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import EditDiscussionMetadataModal from './components/EditDiscussionMetadataModal';
import { discussionTypeOptions, discussionTypeLabel, defaultDiscussionType } from './discussionTypeOptions';
import './lockReason';
import './flagsModeration';
import './discussionTypeFilter';

// In Flarum 2.x, DiscussionComposer is code-split into a lazy-loaded async
// chunk, so it is NOT available at initializer time. We therefore extend it by
// module path (string form): `extend` defers the patch via `flarum.reg.onLoad`
// until the chunk loads — the same pattern bundled extensions (e.g. flarum/tags)
// use. Passing `DiscussionComposer.prototype` directly would be `undefined` at
// boot and throw. `Discussion`/`Model` live in the main bundle, so the model
// attribute can be assigned directly.
const DISCUSSION_COMPOSER = 'flarum/forum/components/DiscussionComposer';
const DISCUSSION_HERO = 'flarum/forum/components/DiscussionHero';
const DISCUSSION_LIST_ITEM = 'flarum/forum/components/DiscussionListItem';
app.initializers.add('bhic-discussion-metadata', () => {
  // Expose the BHIC metadata attributes on the Discussion model. Each maps to
  // its snake_case column — part of the external BHIC/Memorix integration.
  Discussion.prototype.sanityId = Model.attribute('sanityId');
  Discussion.prototype.discussionType = Model.attribute('discussionType');
  Discussion.prototype.memorixId = Model.attribute('memorixId');

  // Hold the composer input values.
  extend(DISCUSSION_COMPOSER, 'oninit', function () {
    this.sanityId = Stream('');
    this.discussionType = Stream(defaultDiscussionType());
    this.memorixId = Stream('');
  });

  // Show the inputs in the composer header — only if the actor may manage
  // metadata.
  extend(DISCUSSION_COMPOSER, 'headerItems', function (items) {
    if (!app.forum.attribute('canManageMetadataForDiscussion')) return;
    items.add('bhicMetadata', m("div", {
      className: "Form-group BhicMetadataInputs"
    }, m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('bhic.forum.composer.discussion_type_label')), m(Select, {
      value: this.discussionType(),
      options: discussionTypeOptions(),
      onchange: this.discussionType
    })), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('bhic.forum.composer.sanity_id_label')), m("input", {
      className: "FormControl",
      bidi: this.sanityId,
      placeholder: extractText(app.translator.trans('bhic.forum.composer.sanity_id_placeholder'))
    })), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('bhic.forum.composer.memorix_id_label')), m("input", {
      className: "FormControl",
      bidi: this.memorixId,
      placeholder: extractText(app.translator.trans('bhic.forum.composer.memorix_id_placeholder'))
    }))), -10);
  });

  // Include the values in the payload sent to the API. Only non-empty values
  // are attached (a non-permitted actor never renders the inputs, so nothing
  // is sent for them).
  extend(DISCUSSION_COMPOSER, 'data', function (data) {
    const sanityId = this.sanityId && this.sanityId();
    if (sanityId) data.sanityId = sanityId;
    const discussionType = this.discussionType && this.discussionType();
    if (discussionType) data.discussionType = discussionType;
    const memorixId = this.memorixId && this.memorixId();
    if (memorixId) data.memorixId = memorixId;
  });

  // Add an "Edit BHIC metadata" button to the discussion moderation controls
  // (same menu as Rename / Edit Tags), shown only to actors who may manage
  // metadata. Opens a modal that PATCHes the discussion. `DiscussionControls`
  // is a plain util in the main bundle, so it can be extended directly.
  extend(DiscussionControls, 'moderationControls', function (items, discussion) {
    if (!app.forum.attribute('canManageMetadataForDiscussion')) return;
    items.add('bhicMetadata', m(Button, {
      icon: "fas fa-database",
      onclick: () => app.modal.show(EditDiscussionMetadataModal, {
        discussion
      })
    }, app.translator.trans('bhic.forum.edit_metadata.button')));
  });

  // Show the type beside a discussion's meta in the list, where flarum/tags
  // puts its tag labels. Ordinary discussions carry the default type and say
  // nothing, same as on the discussion page.
  extend(DISCUSSION_LIST_ITEM, 'infoItems', function (items) {
    const discussionType = this.attrs.discussion.discussionType();
    if (!discussionType) return;
    items.add('bhicDiscussionType', m("span", {
      className: "DiscussionTypeLabel"
    }, discussionTypeLabel(discussionType)), 10);
  });

  // Display the values on the discussion page. The fields are readable by any
  // user, so these show for everyone whenever a value is set.
  extend(DISCUSSION_HERO, 'items', function (items) {
    const discussion = this.attrs.discussion;
    const sanityId = discussion.sanityId();
    if (sanityId) {
      items.add('bhicSanityId', m("div", {
        className: "DiscussionHero-sanityId"
      }, app.translator.trans('bhic.forum.hero.sanity_id', {
        value: sanityId
      })), 5);
    }

    // Every discussion has a type now, so only a non-default one is worth showing.
    const discussionType = discussion.discussionType();
    if (discussionType && discussionType !== defaultDiscussionType()) {
      items.add('bhicDiscussionType', m("div", {
        className: "DiscussionHero-discussionType"
      }, app.translator.trans('bhic.forum.hero.discussion_type', {
        value: discussionTypeLabel(discussionType)
      })), 5);
    }
    const memorixId = discussion.memorixId();
    if (memorixId) {
      items.add('bhicMemorixId', m("div", {
        className: "DiscussionHero-memorixId"
      }, app.translator.trans('bhic.forum.hero.memorix_id', {
        value: memorixId
      })), 5);
    }
  });
});